/**
 * CAOS-686 — the pure reading rules a conversation surface needs: which day a message belongs to,
 * how that day is said, and whether one message continues the one above it.
 *
 * Shared code, not a component — it registers no custom element, so it gets no manifest and is not
 * placeable. Everything here is a pure function of its arguments, including the clock, which is
 * passed rather than read. A day label that reads `new Date()` for itself cannot be tested for the
 * boundary it exists to draw: "Yesterday" is only correct relative to a moment, and the moment a
 * test cares about is never the moment it runs.
 *
 * These are the rules the reference conversation surface arrived at over its own life, kept because
 * they are the conventions of the genre rather than one app's preference: a day divider that says
 * Today and Yesterday in words before it starts saying dates, and consecutive messages from one
 * author within a few minutes drawn as one block rather than as a stack of identical headers.
 */

/** The calendar day a moment falls in, in the reader's own zone, as a sortable key. */
export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * How a day divider says which day it is: `Today`, `Yesterday`, then a weekday and date, gaining
 * the year once the day is not in the current one.
 *
 * `now` is an argument. See the module header — the whole substance of this function is where it
 * puts the boundaries, and a function that read the clock itself could only ever be tested on the
 * day it ran.
 *
 * The date itself is formatted by the runtime for the reader's own locale rather than in English:
 * the label is read by whoever opens the conversation, and this library draws for an org that
 * chooses its own language. `Today` and `Yesterday` are the caller's to translate — they are the
 * two the runtime cannot produce from a date alone — so they are returned as the marker strings
 * `today` and `yesterday` by {@link dayDividerKind}, and this function's words are the default.
 */
export function dayLabel(at: Date, now: Date, locale?: string): string {
  const kind = dayDividerKind(at, now);
  if (kind === 'today') return 'Today';
  if (kind === 'yesterday') return 'Yesterday';
  const sameYear = at.getFullYear() === now.getFullYear();
  return at.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/**
 * Which of the three things a day divider is saying. Separated from {@link dayLabel} so a caller
 * with its own words for the first two — a localised surface, or one that says "Earlier today" —
 * does not have to match strings against a label to find out which case it is in.
 */
export function dayDividerKind(at: Date, now: Date): 'today' | 'yesterday' | 'date' {
  const key = dayKey(at);
  if (key === dayKey(now)) return 'today';
  const yesterday = new Date(now.getTime());
  yesterday.setDate(yesterday.getDate() - 1);
  if (key === dayKey(yesterday)) return 'yesterday';
  return 'date';
}

/** The default gap, in minutes, within which one author's messages are drawn as one block. */
export const GROUPING_GAP_MINUTES = 5;

/**
 * Does this message continue the one above it — same author, close enough in time, and on the same
 * day?
 *
 * The day check is here rather than at the call site because a divider between two messages and
 * those two messages being drawn as one block are contradictory statements about the same pair. A
 * grouping rule that considered only the author and a five-minute gap would happily group across a
 * midnight boundary and put the header on the wrong side of the divider.
 *
 * A NEGATIVE gap — a message stamped before the one above it — does not group. Out-of-order
 * timestamps happen (a clock correction, an import, a message delivered late), and a rule written
 * as "within five minutes" in absolute terms would silently group a message under an author it
 * does not follow.
 */
export function continuesPrevious(
  previous: { author: string; at: Date },
  current: { author: string; at: Date },
  gapMinutes: number = GROUPING_GAP_MINUTES,
): boolean {
  if (previous.author !== current.author) return false;
  if (dayKey(previous.at) !== dayKey(current.at)) return false;
  const gap = current.at.getTime() - previous.at.getTime();
  return gap >= 0 && gap <= gapMinutes * 60_000;
}

/**
 * The time of day a message header shows, in the reader's own locale. Short by design: the day is
 * already said by the divider above, so repeating it on every message is noise.
 */
export function timeLabel(at: Date, locale?: string): string {
  return at.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

/**
 * How far from the bottom of a scrolling list counts as "still reading the latest", in pixels.
 *
 * Named rather than inlined because TWO behaviours read it and they must agree: whether an arriving
 * message scrolls the list, and whether the jump-to-latest control appears. Two separate constants
 * would eventually differ, and the gap between them is a band where a new message neither scrolls
 * into view nor offers a way to reach it.
 */
export const NEAR_BOTTOM_PX = 200;

/** Is this scroller close enough to its end to be following the conversation? */
export function isNearBottom(
  metrics: { scrollHeight: number; scrollTop: number; clientHeight: number },
  threshold: number = NEAR_BOTTOM_PX,
): boolean {
  return metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight < threshold;
}
